import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import SendIcon from '@mui/icons-material/Send';
import { bugCommentsApi } from '../../api/acceptanceApi';
import { getApiErrorMessage, resolveBugImageUrl } from '../../api/client';
import { notify } from '../../components/Notifier';
import { ImageUploader } from '../../components/ImageUploader';
import { SavedImageGrid } from '../../components/SavedImageGrid';
import { formatDateTime } from '../../utils/date';
import type { BugComment } from '../../types';

interface BugCommentThreadProps {
  bugId: string;
  /** Hiện ô soạn bình luận (cần permission BUG_COMMENT). */
  canComment: boolean;
  /** Đổi giá trị để nạp lại danh sách khi bình luận được tạo từ nơi khác. */
  reloadKey?: number;
}

/**
 * Khu "Trao đổi" trong dialog chi tiết lỗi: khách hàng và đội xử lý bình luận
 * qua lại, kèm ảnh chụp màn hình. Append-only — không sửa, không xoá.
 */
export function BugCommentThread({
  bugId,
  canComment,
  reloadKey = 0,
}: BugCommentThreadProps): JSX.Element {
  const [comments, setComments] = useState<BugComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    bugCommentsApi
      .list(bugId)
      .then((items) => {
        if (!cancelled) setComments(items);
      })
      .catch(() => {
        if (!cancelled) notify('Không tải được bình luận', 'warning');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [bugId, reloadKey]);

  const send = async () => {
    const text = body.trim();
    if (!text) return;
    setSending(true);
    try {
      const created = await bugCommentsApi.create(bugId, text, files);
      setComments((prev) => [...prev, created]);
      setBody('');
      setFiles([]);
    } catch (err) {
      notify(getApiErrorMessage(err, 'Không gửi được bình luận'), 'error');
    } finally {
      setSending(false);
    }
  };

  return (
    <Stack spacing={1.5}>
      <Typography variant="subtitle2" fontWeight={700}>
        Trao đổi
      </Typography>

      {loading ? (
        <Typography variant="body2" color="text.secondary">
          Đang tải bình luận…
        </Typography>
      ) : comments.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Chưa có bình luận nào.
        </Typography>
      ) : (
        <Stack spacing={1.5}>
          {comments.map((comment) => (
            <Box
              key={comment.id}
              sx={{ borderLeft: 2, borderColor: 'divider', pl: 1.5, py: 0.5 }}
              data-testid="bug-comment"
            >
              <Typography variant="caption" color="text.secondary">
                {comment.author_name ?? 'Không rõ'} · {formatDateTime(comment.created_at)}
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {comment.body}
              </Typography>
              <SavedImageGrid images={comment.images} resolveUrl={resolveBugImageUrl} size={72} />
            </Box>
          ))}
        </Stack>
      )}

      {canComment && (
        <Stack spacing={1}>
          <TextField
            label="Viết bình luận"
            placeholder="Mô tả thêm, phản hồi hoặc hỏi lại đội xử lý…"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            multiline
            minRows={2}
            fullWidth
            size="small"
            disabled={sending}
          />
          {/*
            Nút đính ảnh nằm cạnh nút Gửi (ImageUploader ở chế độ compact): khu
            bình luận là phần phụ của dialog, khung kéo-thả đầy đủ chiếm quá
            nhiều chỗ. Ảnh xem trước hiện ngay dưới nút khi đã chọn.
          */}
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <ImageUploader value={files} onChange={setFiles} disabled={sending} compact />
            <Button
              variant="contained"
              size="small"
              startIcon={<SendIcon />}
              disabled={sending || body.trim() === ''}
              onClick={() => void send()}
            >
              {sending ? 'Đang gửi…' : 'Gửi'}
            </Button>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
}
